import { useCallback, useEffect, useRef, useState } from 'react';
import { MASCOT_SHARE_HIDE_EVENT, MASCOT_SHARE_HIDE_READY_EVENT, MASCOT_SHARE_HIDE_SET_EVENT, MASCOT_SHARE_HIDE_WAIT_MS, parseMascotShareHideSnapshot, } from './mascotShareHide.js';
const UNAVAILABLE = { state: 'unavailable', snapshot: null, pending: false, error: null, set: () => { } };
/**
 * The native "Hide the mascot while I share my screen in Teams" setting as the desktop shell relays it
 * (mascotShareHide.ts). Only with `enabled` (the desktop workspace): elsewhere the state is 'unavailable' and nothing is
 * asked. The widget shows only what the agent last said; a change is a request, answered by the next snapshot, or an
 * error when none comes in time. An older agent that has no such setting never answers, and the control is not shown.
 */
export function useMascotShareHide(enabled) {
    const [snapshot, setSnapshot] = useState(null);
    const [state, setState] = useState('loading');
    const [pending, setPending] = useState(false);
    const [error, setError] = useState(null);
    const answer = useRef(undefined);
    const set = useCallback((next) => {
        if (!enabled)
            return;
        setPending(true);
        setError(null);
        clearTimeout(answer.current);
        answer.current = setTimeout(() => {
            setPending(false);
            setError('The desktop app did not answer. Try again.');
        }, MASCOT_SHARE_HIDE_WAIT_MS);
        window.dispatchEvent(new CustomEvent(MASCOT_SHARE_HIDE_SET_EVENT, { detail: { enabled: next } }));
    }, [enabled]);
    useEffect(() => {
        if (!enabled)
            return;
        setSnapshot(null);
        setState('loading');
        setPending(false);
        setError(null);
        const initial = setTimeout(() => setState((current) => (current === 'ready' ? current : 'unavailable')), MASCOT_SHARE_HIDE_WAIT_MS);
        const receive = (event) => {
            const next = parseMascotShareHideSnapshot(event.detail);
            if (!next)
                return;
            clearTimeout(initial);
            clearTimeout(answer.current);
            setSnapshot(next);
            setState('ready');
            setPending(false);
            setError(null);
        };
        window.addEventListener(MASCOT_SHARE_HIDE_EVENT, receive);
        window.dispatchEvent(new CustomEvent(MASCOT_SHARE_HIDE_READY_EVENT));
        return () => {
            window.removeEventListener(MASCOT_SHARE_HIDE_EVENT, receive);
            clearTimeout(initial);
            clearTimeout(answer.current);
        };
    }, [enabled]);
    return enabled ? { state, snapshot, pending, error, set } : UNAVAILABLE;
}
//# sourceMappingURL=useMascotShareHide.js.map